import { useCallback, useEffect, useState } from 'react';
import notifyHB from '@/config/notifyHB';
import {
	isOnboardingStep,
	OnboardingStatus,
} from '@/entities/onboarding-status';
import { isHttpError } from '@/http';
import { getOnboardingStatus } from '@/use-cases/get-onboarding-status';
import { getUserInfo } from '@/use-cases/get-user-info';
import {
	decideOnboardingEntry,
	ONBOARDING_STEP_CACHE_KEY,
	OnboardingEntry,
	parseCachedStep,
} from './onboarding-resume';

const report = (error: unknown) =>
	notifyHB('request-signature', 'useOnboardingResume', error);

/** Status of the active process GET /user points at, if any. */
async function loadActiveProcessStatus(
	skipUlid: string | null,
): Promise<OnboardingStatus | null> {
	const profile = await getUserInfo();
	if (
		profile.ActiveProcessType !== 'onboard' ||
		!profile.ActiveProcessUlid ||
		profile.ActiveProcessUlid === skipUlid
	) {
		return null;
	}
	return getOnboardingStatus(profile.ActiveProcessUlid);
}

/**
 * The ulid login stored is tried first. When there is none, or it is not the
 * caller's (404), the active process from GET /user is used instead, which is
 * how certificate holders, who login does not seed, are found. A stored ulid
 * that reads COMPLETED is no longer the active process, so the active one
 * (which may be in review) wins over it.
 */
async function loadInitialStatus(): Promise<OnboardingStatus | null> {
	const storedUlid = localStorage.getItem('onboarding-ulid');
	let storedStatus: OnboardingStatus | null = null;
	if (storedUlid) {
		try {
			storedStatus = await getOnboardingStatus(storedUlid);
			if (storedStatus.step !== 'COMPLETED') {
				return storedStatus;
			}
		} catch (error) {
			if (!isHttpError(error) || error.status !== 404) {
				report(error);
				return null;
			}
			localStorage.removeItem('onboarding-ulid');
		}
	}

	try {
		return (await loadActiveProcessStatus(storedUlid)) ?? storedStatus;
	} catch (error) {
		report(error);
		return storedStatus;
	}
}

/**
 * Resolves where the wizard opens from the backend status. `entry` stays null
 * until then, so no step (and no payment checkout) renders before it.
 */
export function useOnboardingResume(isNewSignatureRequest: boolean) {
	const [entry, setEntry] = useState<OnboardingEntry | null>(null);
	const [status, setStatus] = useState<OnboardingStatus | null>(null);

	// Runs once: the entry is decided from the URL the wizard was opened with.
	useEffect(() => {
		let cancelled = false;
		loadInitialStatus().then((initialStatus) => {
			if (cancelled) return;
			if (initialStatus && !isOnboardingStep(initialStatus.step)) {
				report(
					new Error(`Unknown onboarding step: ${initialStatus.step}`),
				);
			}
			setStatus(initialStatus);
			setEntry(
				decideOnboardingEntry({
					status: initialStatus,
					cachedStep: parseCachedStep(
						localStorage.getItem(ONBOARDING_STEP_CACHE_KEY),
					),
					isNewSignatureRequest,
				}),
			);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	/** Re-reads the status of the stored process; null when unavailable. */
	const refresh = useCallback(async () => {
		const ulid = localStorage.getItem('onboarding-ulid');
		if (!ulid) {
			return null;
		}
		try {
			const refreshed = await getOnboardingStatus(ulid);
			setStatus(refreshed);
			return refreshed;
		} catch (error) {
			report(error);
			return null;
		}
	}, []);

	/**
	 * Resolves the status like the first load, falling back to the active
	 * process from GET /user. A conflict on the new-certificate flow may point
	 * at a process this browser has no ulid for.
	 */
	const resolve = useCallback(async () => {
		const resolved = await loadInitialStatus();
		if (resolved) setStatus(resolved);
		return resolved;
	}, []);

	return { entry, setEntry, status, refresh, resolve };
}
